import type React from 'react';

import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import { TypewriterPlaceholder } from '../TypewriterPlaceholder';

/** Phrases the hero cycles through. Module-level literals, so the i18n key extractor can see them. */
const HERO_TYPING_PHRASES = [
	translationKey('corgi.homepage.hero_section.input_prompt_placeholder_1'),
	translationKey('corgi.homepage.hero_section.input_prompt_placeholder_2'),
	translationKey('corgi.homepage.hero_section.input_prompt_placeholder_3'),
	translationKey('corgi.homepage.hero_section.input_prompt_placeholder_4'),
] as const;

export type HeroTypingOverlayProps = {
	/** `aria-describedby` target on the textarea: the typed overlay is aria-hidden, so this carries the hint. */
	descriptionId: string;
};

/**
 * The typed placeholder as the hero fields use it, positioned over `PromptInput`'s textarea.
 *
 * The consuming `PromptInput` needs the `group` class: the overlay hides on `textarea:focus` so the
 * real caret isn't shown beside the typed one. Its left inset matches the textarea's `px-5`; the
 * right inset is wider to clear the submit button, which a second line would otherwise run under.
 */
export const HeroTypingOverlay: React.FC<HeroTypingOverlayProps> = ({ descriptionId }) => {
	const { translate } = useI18n();

	return (
		<>
			<span id={descriptionId} className="sr-only">
				{translate('corgi.homepage.hero_section.input_prompt_placeholder')}
			</span>
			<TypewriterPlaceholder
				phrases={HERO_TYPING_PHRASES}
				className="duration-t2 absolute top-6 right-16 left-5 transition-opacity group-has-[textarea:focus]:opacity-0"
			/>
		</>
	);
};
